import React, { useMemo } from 'react';
import { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import EDTypography from '../../../../../../../BaseComponents/Typography/EDTypography';
import EkonomiskAnalysLogo from '../EkonomiskAnalysLogo/EkonomiskAnalysLogo';
import EDIconButton from '../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import {
  ISelectedAnalys,
  ISelectedAnalysCodePrivilege,
} from '../../EkonomiskAnalysPage';
import { ekanalys_ekanalys_columns } from '../../Queries/EkonomiskAnalysSettingsQuery/_QueryTypes/ekanalys';
import { ISumData } from '../../Queries/EkonomiskAnalysSettingsQuery/EkonomiskAnalysSettingsQuery';
import { IUIUrval } from '../../../../../../../Types/UrvalTypes';
import { ILocalAnalys } from '../EkonomiskAnalysMain/EkonomiskAnalysMain';
import { IEkonomiskAnalysFilledDates } from '../AnalysSettingsHandler/AnalysSettingsHandler';
import EkonomiskAnalysHeaderFunctionButton from '../EkonomiskAnalysHeaderFunctionButton/EkonomiskAnalysHeaderFunctionButton';
import { AnalysInitQuery_logo } from '../../Queries/EkonomiskAnalysInitQuery/_QueryTypes/AnalysInitQuery';

interface IProps {
  selectedAnalys: ISelectedAnalys;
  database: string;
  anvandarUrval: IUIUrval | null;
  currentDates: IEkonomiskAnalysFilledDates;
  updatedSettings: ILocalAnalys;
  isChanges: boolean;
  periodsComponent: (
    dense?: boolean,
    shouldUpdateDirect?: boolean,
    simple?: boolean,
    fromDateRef?: React.RefObject<HTMLInputElement | null>,
    toDateRef?: React.RefObject<HTMLInputElement | null>,
    isChanges?: boolean
  ) => React.ReactNode;
  showChartModal?: () => void;
  showExcelModal?: () => void;
  togglePDFModal: () => void;
  updateReport: () => void;
  endastBokfort?: boolean;
  forceHelarsjamforelse: boolean | null;
  columns?: ekanalys_ekanalys_columns[];
  sums?: ISumData[];
  isLopandeAnalys?: boolean;
  impersonatedCodePrivilege?: ISelectedAnalysCodePrivilege;
  baseSelectionId?: string;
  detaljUrval?: IUIUrval;
  fromDateRef: React.RefObject<HTMLInputElement | null>;
  toDateRef: React.RefObject<HTMLInputElement | null>;
  toggleColumnSelectOpen?: () => void;
  logo: AnalysInitQuery_logo;
  sammanfattningsMode: boolean;
}

const useStyles = makeStyles()(
  (theme: Theme) =>
    ({
      container: {
        position: 'sticky', // fixme: kanske onödig?
        top: 0,
        left: 0,
        margin: 0,
        padding: '24px 0 0',
        zIndex: 100,
        backgroundColor: theme.palette.common.white,
      },
      root: {
        display: 'flex',
        justifyContent: 'space-between',
      },
      titleContainer: {
        display: 'flex',
        flex: '2 1 auto',
        justifyContent: 'center',
        alignItems: 'flex-start',
      },
      sideContainer: {
        display: 'flex',
        flexDirection: 'column',
        flex: '0 1 25%',
      },
      sideContainerHalf: {
        display: 'flex',
        flexDirection: 'column',
        flex: '0 1 50%',
      },
      leftSide: {
        alignItems: 'flex-start',
      },
      rowContainerMargin: {
        marginTop: -14,
        alignItems: 'flex-end',
      },
      rowContainer: {
        display: 'flex',
        alignItems: 'flex-start',
        whiteSpace: 'nowrap',
      },
      inline: {
        display: 'inline',
        whiteSpace: 'nowrap',
      },
      marginRight: {
        marginRight: theme.spacing(5),
      },
      menuHeaderIcon: {
        marginRight: theme.spacing(3),
      },
      menuHeader: {
        margin: theme.spacing(1, 0),
      },
      titleBPoint: {
        minWidth: 300,
        hyphens: 'auto',
        [theme.breakpoints.down('lg')]: {
          marginRight: theme.spacing(2),
          minWidth: 185,
        },
      },
      titleCaptionBPoint: {
        [theme.breakpoints.down('lg')]: {
          fontSize: '1.2rem',
          fontWeight: 500,
          lineHeight: 1.1,
          marginRight: 0,
          wordBreak: 'break-word',
        },
      },
      endastBokfort: {
        borderBottom: '1px solid ' + theme.customProperties.Warning.main,
      },
      warningCaption: {
        color: theme.customProperties.Warning.dark,
      },
      warningCaptionLarge: {
        [theme.breakpoints.up('md')]: {
          display: 'block',
        },
        [theme.breakpoints.down('md')]: {
          display: 'none',
        },
      },
      warningCaptionSmall: {
        [theme.breakpoints.up('md')]: {
          display: 'none',
        },
        [theme.breakpoints.down('md')]: {
          display: 'block',
        },
      },
      lockIcon: {
        display: 'inline-block',
        position: 'relative',
        top: -3,
        marginLeft: 4,
      },
      lockIconWarning: {
        display: 'inline-block',
        position: 'relative',
        top: -3,
        marginLeft: 4,
        color: theme.customProperties.Warning.dark,
      },
      cursor: {
        cursor: 'pointer',
      },
      quickActionColumnContainer: {
        alignSelf: 'center',
      },
      disabled: {
        '& svg': {
          opacity: 1,
        },
      },
    } as const)
);

/**
 * En header till en analys tabellvy
 */
const EkonomiskAnalysHeader: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const { classes, cx } = useStyles();

  const valjKolumnerDisabledReason = useMemo(() => {
    if (!props.isLopandeAnalys) {
      return 'Ej aktiv i radbaserade analyser';
    }
    if (props.sammanfattningsMode) {
      return 'Ej tillgängligt i rapportkategori Detaljanalyser';
    }
    return undefined;
  }, [props.isLopandeAnalys, props.sammanfattningsMode]);

  return (
    <div className={classes.container}>
      <div className={classes.root}>
        <div className={cx(classes.sideContainer, classes.leftSide)}>
          <div className={cx(classes.rowContainer)}>
            <div>
              <EkonomiskAnalysLogo logo={props.logo} />
            </div>
          </div>
        </div>

        <div className={cx(classes.titleContainer, classes.titleBPoint)}>
          <div>
            <EDTypography
              variant="h5"
              className={cx(classes.titleCaptionBPoint, {
                [classes.endastBokfort]: !props.endastBokfort,
              })}
              data-testid={'analysHeaderTitle'}
            >
              {props.selectedAnalys.analys.name}
            </EDTypography>
            {!props.endastBokfort && (
              <EDTypography
                variant="caption"
                color="inherit"
                className={classes.warningCaption}
              >
                <span className={classes.warningCaptionLarge}>
                  Inkluderar ej definitivbokförda journaler
                </span>
                <span className={classes.warningCaptionSmall}>
                  Inkl ej def.bokf journaler
                </span>
              </EDTypography>
            )}
          </div>

          {!props.endastBokfort && (
            <span className={classes.warningCaption}>
              <EDIconButton
                icon="warning"
                iconColor="inherit"
                margin="none"
                padding="dense"
                strictHeight
                disableBackground
                disableRipple
                tooltip="Du har valt att visa även ej definitivbokförda journaler i analysen."
                maskButton
              />
            </span>
          )}
        </div>

        <div className={cx(classes.sideContainer, classes.rowContainerMargin)}>
          <div className={classes.rowContainer}>
            <span className={classes.quickActionColumnContainer}>
              <EDIconButton
                className={cx({
                  [classes.disabled]: valjKolumnerDisabledReason !== undefined,
                })}
                color="primary"
                disabled={valjKolumnerDisabledReason !== undefined}
                disabledReason={valjKolumnerDisabledReason}
                icon="view_column"
                margin="normal"
                noLeftMargin
                noTopMargin
                onClick={props.toggleColumnSelectOpen}
                padding="dense"
                tooltip="Välj Kolumner"
              />
            </span>
            {props.periodsComponent(
              true,
              true,
              undefined,
              props.fromDateRef,
              props.toDateRef,
              props.isChanges
            )}
            <EkonomiskAnalysHeaderFunctionButton
              selectedAnalys={props.selectedAnalys}
              isLopandeAnalys={props.isLopandeAnalys}
              showChartModal={props.showChartModal}
              showExcelModal={props.showExcelModal}
              togglePDFModal={props.togglePDFModal}
              isChanges={props.isChanges}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EkonomiskAnalysHeader;
